<template>
  <DefaultLayout v-if="dataCollectionRecord && auditDetail">
    <vue-headful :title="getPageTitle('Data Collection')" />
    <div
      :key="currentComponentKey"
      class="container"
      :class="$style.container"
      v-if="auditCriteria && auditSites && auditConfig"
    >
      <nav
        class="breadcrumb has-succeeds-separator m-t-1"
        aria-label="breadcrumbs"
      >
        <ul>
          <li>
            <router-link :to="getProjectPath(auditDetail.project.id)">
              {{ getFormattedTitle(auditDetail.project.title) }}
            </router-link>
          </li>
          <li>
            <router-link :to="getAuditPath(auditDetail.id)">
              {{ getFormattedTitle(auditDetail.name) }}
            </router-link>
          </li>
          <li class="is-active">
            <router-link to="/">
              {{ breadcrumbTitle }}
            </router-link>
          </li>
        </ul>
      </nav>

      <div :class="$style.titleSection">
        <h1 :class="['oswald-font', $style.title]">
          {{ newEntryTitle ? 'Submit New Record' : 'Edit Record' }}
        </h1>

        <b-field v-if="newEntryTitle">
          <p class="control">
            <b-button
              @click="switchMode('aggregate')"
              :class="[
                $style.button,
                dataCollectionType === 'aggregate' && $style.buttonActive,
              ]"
            >
              <img
                :src="
                  require(dataCollectionType === 'aggregate'
                    ? '@/assets/icons/ic_aggregated_white.png'
                    : '@/assets/icons/ic_aggregated_grey.png')
                "
              />
              Aggregate
            </b-button>
          </p>
          <p class="control">
            <b-button
              @click="switchMode('individual')"
              :class="[
                $style.button,
                dataCollectionType === 'individual' && $style.buttonActive,
              ]"
            >
              <img
                :src="
                  require(dataCollectionType === 'individual'
                    ? '@/assets/icons/ic_individual_white.png'
                    : '@/assets/icons/ic_individual_grey.png')
                "
              />
              Individual
            </b-button>
          </p>
        </b-field>

        <div class="buttons has-addons" v-else>
          <button
            v-if="isPreviousEnabled"
            class="button"
            :class="$style.linkButton"
            @click="redirectToPrevious"
          >
            <span class="icon">
              <i class="fas fa-chevron-left"></i>
            </span>
            <span>Prev</span>
          </button>
          <button
            v-if="isNextEnabled"
            class="button"
            :class="$style.linkButton"
            @click="redirectToNext"
          >
            <span>Next</span>
            <span class="icon">
              <i class="fas fa-chevron-right"></i>
            </span>
          </button>
        </div>
      </div>

      <div :class="$style.dataContainer">
        <p :class="$style.title">{{ auditDetail.project.title }}</p>
        <p :class="$style.name">{{ auditDetail.name }}</p>
        <p :class="$style.type">
          <img
            :class="$style.icon"
            :src="
              require(dataCollectionType === 'aggregate'
                ? '@/assets/icons/ic_aggregated_grey.png'
                : '@/assets/icons/ic_individual_grey.png')
            "
          />
          <span>{{ dataCollectionType }}</span>
        </p>

        <template v-if="dataCollectionType === 'aggregate' && renderComponent">
          <DataCollectionAggregate
            v-if="defaultSiteId !== undefined"
            :auditDetail="auditDetail"
            :editDataCollectionRecord="editDataCollectionRecord"
            :defaultSiteId="defaultSiteId"
            :siteList="siteList"
            :auditCriteriaList="auditCriteriaList"
            :auditConfig="auditConfig"
            :isEdit="true"
            @submitRecords="handleEnteredDataRecords"
            @updateSiteDropdownItem="handleSiteDropdownItem"
          />
        </template>
        <template v-if="dataCollectionType === 'individual' && renderComponent">
          <DataCollectionIndividual
            v-if="defaultSiteId !== undefined"
            :auditDetail="auditDetail"
            :editDataCollectionRecord="editDataCollectionRecord"
            :defaultSiteId="defaultSiteId"
            :siteList="siteList"
            :auditCriteriaList="auditCriteriaList"
            :auditConfig="auditConfig"
            :isEdit="true"
            @submitRecords="handleEnteredDataRecords"
            @updateSiteDropdownItem="handleSiteDropdownItem"
          />
        </template>
      </div>
    </div>
  </DefaultLayout>
</template>
<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import {
  AuditCriteria,
  AuditDetail,
  AuditSamplingRequestPayload,
  AuditSamplingResponse,
  AuditSite,
  AuditSiteListDto,
  DataCollectionRecord,
  DataCollectionSubmitRequestPayload,
} from '@/store/modules/audits/types/audits.types';
import { Action, State } from 'vuex-class';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import { ToastProgrammatic as Toast } from 'buefy';
import DataCollectionAggregate from '@/views/Audit/components/DataCollectionAggregate.vue';
import DataCollectionIndividual from '@/views/Audit/components/DataCollectionIndividual.vue';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { useUserEmail } from '@/utils/user.util';
import { SortOrder } from '@/store/types/general.types';
import { UserRoleInAudit } from '@/store/modules/projects/types/projects.types';
import { orderByUtil } from '@/jbi-shared/util/sort.util';

const dirtyMonths = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sept',
  'Oct',
  'Nov',
  'Dec',
];

@Component({
  components: {
    DefaultLayout,
    DataCollectionAggregate,
    DataCollectionIndividual,
  },
})
export default class DataCollectionPage extends Vue {
  public singleAuditCriteriaId!: number;
  public editDataCollectionRecord!: any;

  public dataCollectionType: string = 'aggregate';
  public newEntryTitle: boolean = true;
  public renderComponent: boolean = true;
  public breadcrumbTitle: string = 'Submit New Record';
  public defaultSiteId: number | null = null;
  public isNextEnabled: boolean = false;
  public isPreviousEnabled: boolean = false;
  public auditDetail: AuditDetail | null = null;
  public currentComponentKey: number = Math.floor(Math.random() * 999);
  public isDataCollector: boolean = false;

  @Action('audits/getAuditDetail')
  public getAuditDetail!: (auditId: number) => void;

  @State((state) => state.audits.auditDetail)
  public stateAuditDetail!: AuditDetail;

  @Action('audits/getAuditCriteria')
  public getAuditCriteria!: (auditId: number) => void;

  @State((state) => state.audits.auditCriteria)
  public auditCriteria!: AuditCriteria[];

  @Action('audits/getAuditSitesForDataCollection')
  public getAuditSitesForDataCollection!: (auditId: number) => void;

  @State((state) => state.audits.auditSites)
  public auditSites!: AuditSite[];

  @Action('audits/postDataCollection')
  public fetchDataCollection!: (
    payload: DataCollectionSubmitRequestPayload,
  ) => void;

  @State((state) => state.audits.dataCollectionResponse)
  public dataCollectionResponse!: any;

  @Action('audits/getSamplingConfig')
  public getSamplingConfig!: (payload: AuditSamplingRequestPayload) => void;

  @State((state) => state.audits.auditConfig)
  public auditConfig!: AuditSamplingResponse;

  @Action('audits/getDataCollectionRecord')
  public getDataCollectionRecord!: (payload: {
    auditId: number;
    dataCollectionRecordId: number;
  }) => void;

  @State((state) => state.audits.dataCollectionRecord)
  public dataCollectionRecord!: DataCollectionRecord;

  @Watch('auditSites')
  @isTruthy
  public watchAuditSites(newValue: AuditSite[]) {
    this.defaultSiteId = newValue[0].id;
  }

  @Watch('dataCollectionRecord')
  @isTruthy
  public watchDataCollectionRecord(newValue: DataCollectionRecord) {
    this.newEntryTitle = false;
    this.dataCollectionType = newValue.dataCollectionType;
    this.breadcrumbTitle = 'Edit Record';
    this.editDataCollectionRecord = newValue;
    this.isPreviousEnabled = newValue.previousRecord !== undefined;
    this.isNextEnabled = newValue.nextRecord !== undefined;
  }

  @Watch('stateAuditDetail')
  @isTruthy
  public watchStateAuditDetail(auditDetails: AuditDetail) {
    this.auditDetail = auditDetails;
  }

  @Watch('dataCollectionResponse')
  @isTruthy
  public watchDataCollectionResponse(newValue: any) {
    Toast.open({
      message: `Changes saved`,
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
    const auditId = this.auditId.toString();
    if (this.isDataCollector) {
      this.$router.push({
        path: `/audits/${auditId}/homepage#dataHistory`,
      });
    } else {
      this.$router.push({
        name: 'audit',
        params: {
          auditId,
        },
        hash: '#dataHistory',
      });
    }
  }

  public mounted() {
    this.fetchData();
  }

  public getFormattedTitle(title: string) {
    if (title.length > 45) {
      return title.substring(0, 45) + '...';
    } else {
      return title;
    }
  }

  public fetchData() {
    if (!this.auditDetail) {
      this.getAuditDetail(this.auditId);
    }
    this.getDataCollectionRecord({
      auditId: this.auditId,
      dataCollectionRecordId: this.dataCollectionRecordId,
    });
    this.getAuditCriteria(this.auditId);
    this.getAuditSitesForDataCollection(this.auditId);
    this.getSamplingConfig({
      auditId: this.auditId,
    });
  }

  public handleEnteredDataRecords(
    newValue: DataCollectionSubmitRequestPayload,
  ) {
    if (newValue.aid === '') {
      newValue.aid = null;
    }
    this.fetchDataCollection(newValue);
  }

  public switchMode(newValue: string) {
    this.renderComponent = false;
    this.dataCollectionType = newValue;
    this.$nextTick(() => {
      this.renderComponent = true;
    });
  }

  get siteList(): AuditSiteListDto[] {
    if (!this.auditSites || !this.auditSites.length) {
      return [];
    }

    return this.auditSites.map((participantSite) => {
      return {
        siteId: participantSite.site.id,
        name: participantSite.site.name,
        auditSiteId: participantSite.id,
        id: participantSite.id,
      };
    });
  }

  get auditCriteriaList(): AuditCriteria[] {
    // returning only criteria which were submitted
    const auditCriteria: AuditCriteria[] = [];
    this.auditCriteria.forEach((audit: AuditCriteria) => {
      this.editDataCollectionRecord.dataCollectionRecords.forEach(
        (editedRecord: any) => {
          if (editedRecord.auditCriteria.id === audit.id) {
            auditCriteria.push(audit);
          }
        },
      );
    });

    return auditCriteria.sort(orderByUtil(['order'], [SortOrder.ASC]));
  }

  public handleSiteDropdownItem(newValue: number) {
    this.defaultSiteId = newValue;
  }

  get auditId(): number {
    return +this.$route.params.auditId;
  }

  get dataCollectionRecordId(): number {
    return +this.$route.params.dataCollectionRecordId;
  }

  public redirectToPrevious() {
    if (this.dataCollectionRecord.previousRecord) {
      this.$router.replace({
        params: {
          dataCollectionRecordId: this.dataCollectionRecord.previousRecord.id.toString(),
        },
      });
      this.fetchData();
    }
  }

  public redirectToNext() {
    if (this.dataCollectionRecord.nextRecord) {
      this.$router.replace({
        params: {
          dataCollectionRecordId: this.dataCollectionRecord.nextRecord.id.toString(),
        },
      });
      this.fetchData();
    }
  }

  public getProjectPath(projectId: number) {
    let projectPath = '/projects/' + projectId;
    if (
      this.auditDetail &&
      this.auditDetail.userRoleInAudit === UserRoleInAudit.isDataCollector
    ) {
      projectPath = '/projects/' + projectId + '/homepage';
      this.isDataCollector = true;
    }
    return projectPath;
  }

  public getAuditPath(auditId: number) {
    let auditPath = '/audits/' + auditId;
    if (
      this.auditDetail &&
      this.auditDetail.userRoleInAudit === UserRoleInAudit.isDataCollector
    ) {
      auditPath += '/homepage';
      this.isDataCollector = true;
    }
    return auditPath;
  }

  get userEmail(): string | undefined {
    return useUserEmail.call(this);
  }
}
</script>
<style lang="scss" module>
.container {
  .titleSection {
    margin-top: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: space-between;

    .title {
      color: #00205b;
      font-size: 40px;
      font-weight: 700;
    }
  }

  .button {
    span {
      display: flex;
      align-items: center;
      font-size: 14px;

      img {
        width: 15px;
        height: 14px;
        margin-right: 5px;
      }
    }
  }

  .buttonActive {
    color: white;
    background-color: #00205b;
    border: 1px solid #00205b;

    &:hover {
      color: white;
      border: 1px solid #00205b;
    }
  }

  .linkButton {
    background: transparent;
    border: none;
    padding: 8px 16px;

    span {
      font-weight: normal;
    }

    &:hover {
      color: $primary;
    }
  }
}

.bField .label {
  color: #9f9f9f;
  font-size: 14px;
}

.dataContainer {
  background-color: #ffffff;
  border-radius: 4px;
  box-shadow: 0 0 4px 0 rgba(0, 0, 0, 0.12);
  margin-top: 24px;
  padding: 1.5rem;

  .title {
    font-size: 18px;
    margin-bottom: 1rem;
    word-break: break-all;
  }

  .name {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 1rem;
    word-break: break-all;
  }

  .type {
    display: flex;
    align-items: center;
    text-transform: capitalize;
    font-size: 16px;
    margin-bottom: 1.5rem;

    .icon {
      height: 16px;
      width: 16px;
      margin-right: 0.5rem;
    }
  }
}
</style>
